import React, { useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import { AuthenticationDetails } from "amazon-cognito-identity-js";
import { useNavigate } from "react-router-dom";
import OtpInput from "react-otp-input";
import { Container, Title, Resend } from "../GeneralStyles/GeneralStyles";
import { Paragraph, Success, Icon, ResentMessage, SupportMessage } from "./Styled";
import { useFade } from "../../hooks/useFade";
import Valid from "../../containers/Signup/Assets/Valid.svg";
import { confirmSignUp } from "../../containers/Signup/Signup.service";
import { reportError } from "../../utils/rollbar";
import { analyticsService } from "../../services/AnalyticsService";
import { useSignup } from "../../contexts/SignupContext";

const PhoneConfirmation = () => {
  const { cognitoUser, password, fullNumber } = useSignup();
  const [code, setCode] = useState('');
  const [errored, setErrored] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [codeResent, setCodeResent] = useState(false);
  const [isVisible, setIsVisible, fadeProps] = useFade(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  let navigate = useNavigate();

  const obscurePhone = (phoneNumber: any) => {
    let intLetter = phoneNumber.substring(0, 4);
    let endLetter = phoneNumber.substring(phoneNumber.length - 3, phoneNumber.length);
    return intLetter + "********" + endLetter;
  };

  const authenticate = async (Password: any, CognitoUser: any, Username: any) => {
    await CognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");

    const authDetails = new AuthenticationDetails({
      Username,
      Password,
    });

    await CognitoUser.authenticateUser(authDetails, {
      onSuccess: (data: any) => {
        console.log("2 - Authenticate User", authDetails, data);
        getVerificationCode();
      },
      onFailure: (error: any) => {
        console.log("2 - Error", error);
        reportError("Failed to authenticate user", error);
      },
    });
  };

  const getVerificationCode = async () => {
    await Auth.verifyUserAttribute(cognitoUser, "email")
      .then((data) => {
        console.log("3 - Get Verification Code", cognitoUser, data);
      })
      .catch((err) => {
        console.log("3 - Error", err);
        reportError("Failed to get phone verification code", err);
      });
  };

  const resendVerificationCode = async () => {
    setCodeResent(true);
    try {
      await Auth.resendSignUp(cognitoUser.username);
      analyticsService.track("Signup - User requested new phone verification code", {}, () => {});
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmit = async (code: any) => {
    setCode(code);
    if (code?.length === 6) {
      try {
        analyticsService.track("Signup - User submitted verification code", {}, () => {});
        await confirmSignUp(code, cognitoUser);
        await authenticate(password, cognitoUser, cognitoUser.username);
        if (!errored) {
          navigate("/success");
        }
      } catch (error) {
        console.error("Signup Error: ", error);
        setErrored(true);
  
        const cognitoError = error as { code?: string; message?: string };
  
        if (cognitoError.code === 'UserLambdaValidationException' && cognitoError.message?.includes('Recaptcha verification failed')) {
          reportError("Recaptcha verification failed during signup", cognitoError, 'info');
        } else {
          // Handle other types of errors as 'error'
          reportError("Signup error", cognitoError);
        }
      }
    }
  };  
  
  return (
    <Container {...fadeProps}>
      <Title>Verify your phone number</Title>
      <Paragraph>
        A text message with a verification code was sent to {obscurePhone(fullNumber)}.
        <br />
        Enter the code below to confirm your phone number.
        {codeResent ? (
          <ResentMessage>
            <Icon src={Valid} alt="checkmark" />
            <Resend Color="#008A24">A new code has been sent to you</Resend>
          </ResentMessage>
        ) : (
          <Resend Color="#757584" Decoration="underline" onClick={resendVerificationCode}>
            Resend code
          </Resend>
        )}
      </Paragraph>
      <div className="otp-wrapper" style={{ display: "block" }}>
        <OtpInput
          numInputs={6}
          value={code}
          onChange={handleSubmit}
          inputStyle="otp-code__input"
          shouldAutoFocus={true}
          isInputNum={true}
          hasErrored={errored}
          errorStyle="otp-code__input--error"
          isDisabled={loading}
        />
        {success && <Success>User created and confirmed!</Success>}
        {errored && <h3>Something went wrong!</h3>}
        <SupportMessage>
          If you haven’t received the code, please contact{" "}
          <a href="https://verbit.happyfox.com/new" target="_blank" rel="noreferrer">
            Support Center
          </a>
        </SupportMessage>
      </div>
    </Container>
  );
};

export default PhoneConfirmation;
